// API-key persistence across sessions (until manually cleared).
// - Electron prod: OS keychain via main-process safeStorage (macOS Keychain,
//   Windows DPAPI). Ciphertext-only file; see electron/secureStore.ts.
// - Browser dev (`npm run dev`): localStorage — same-origin, this machine
//   only. Fine for local dev, never for shared machines; the UI says so.
// Model + linger (non-sensitive) always live in localStorage.
// Legacy sessionStorage keys migrate over on first load, then are removed.
import { DEFAULT_GEMINI_MODEL, DEFAULT_MODEL, type LlmProvider } from '../llm/client.js';

const LS_KEY = 'converse:apiKey';
const LS_MODEL = 'converse:model';
const LS_LINGER = 'converse:linger';
const DEFAULT_LINGER = 0.75;

interface ElectronKeyBridge {
  saveApiKey: (provider: LlmProvider, key: string) => Promise<{ ok: boolean; encrypted: boolean }>;
  loadApiKey: (provider: LlmProvider) => Promise<string | null>;
  clearApiKey: (provider: LlmProvider) => Promise<unknown>;
}

function bridge(): ElectronKeyBridge | null {
  const w = window as unknown as { converse?: Partial<ElectronKeyBridge> };
  const c = w.converse;
  return c &&
    typeof c.saveApiKey === 'function' &&
    typeof c.loadApiKey === 'function' &&
    typeof c.clearApiKey === 'function'
    ? (c as ElectronKeyBridge)
    : null;
}

export function isElectronRuntime(): boolean {
  return bridge() !== null;
}

export function keyStorageLabel(): string {
  return isElectronRuntime()
    ? 'OS keychain (macOS Keychain / Windows DPAPI)'
    : 'this browser only (dev mode)';
}

function lsGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null; // private mode etc. — behave as "nothing saved"
  }
}

function lsSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    // Unwritable storage: session simply won't persist. Non-fatal.
  }
}

function lsDel(k: string): void {
  try {
    localStorage.removeItem(k);
  } catch {
    // Already gone for our purposes.
  }
}

// One-time migration from the old sessionStorage keys.
function migrateLegacy(k: string): string | null {
  try {
    const v = sessionStorage.getItem(k);
    if (v != null && lsGet(k) == null) {
      lsSet(k, v);
      sessionStorage.removeItem(k);
      return v;
    }
    return null;
  } catch {
    return null;
  }
}

function browserKeyName(provider: LlmProvider): string {
  return `converse:${provider}:apiKey`;
}

const GEMINI_KEYS_NAME = 'converse:gemini:apiKeys';

function cleanKeys(keys: string[]): string[] {
  return keys.map((k) => k.trim()).filter(Boolean);
}

export async function loadPersistedKey(provider: LlmProvider): Promise<string> {
  const b = bridge();
  if (b) {
    try {
      return (await b.loadApiKey(provider)) ?? '';
    } catch {
      return '';
    }
  }
  const k = browserKeyName(provider);
  // Existing localStorage key was DeepSeek's key.
  return lsGet(k) ?? (provider === 'deepseek' ? lsGet(LS_KEY) ?? migrateLegacy(LS_KEY) : null) ?? '';
}

export async function savePersistedKey(provider: LlmProvider, apiKey: string): Promise<{ encrypted: boolean }> {
  const b = bridge();
  if (b) return b.saveApiKey(provider, apiKey);
  lsSet(browserKeyName(provider), apiKey);
  return { encrypted: false };
}

export async function clearPersistedKey(provider: LlmProvider): Promise<void> {
  const b = bridge();
  if (b) {
    try {
      await b.clearApiKey(provider);
    } catch {
      // Best effort; UI drops its in-memory copy regardless.
    }
    return;
  }
  lsDel(browserKeyName(provider));
  if (provider === 'deepseek') lsDel(LS_KEY);
}

// --- Multi-key variants (Gemini key ring, up to 5 slots) ---
// Browser: JSON array under its own key. Electron vault: the same array
// serialized through the existing string bridge (encryption unchanged).
// Legacy single Gemini key migrates into slot 1.
export async function loadPersistedKeys(provider: LlmProvider): Promise<string[]> {
  const b = bridge();
  if (b) {
    try {
      const raw = (await b.loadApiKey(provider)) ?? '';
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) return cleanKeys(parsed.map(String));
      } catch {
        // Pre-ring single key: fall through to single-key handling.
      }
      return raw.trim() ? [raw.trim()] : [];
    } catch {
      return [];
    }
  }
  if (provider !== 'gemini') {
    const single = await loadPersistedKey(provider);
    return single ? [single] : [];
  }
  try {
    const raw = localStorage.getItem(GEMINI_KEYS_NAME);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return cleanKeys(parsed.map(String));
    }
  } catch {
    // Corrupt array: fall through to legacy single key.
  }
  const legacy = lsGet(browserKeyName('gemini'));
  return legacy?.trim() ? [legacy.trim()] : [];
}

export async function savePersistedKeys(provider: LlmProvider, keys: string[]): Promise<{ encrypted: boolean }> {
  const cleaned = cleanKeys(keys);
  const b = bridge();
  if (b) return b.saveApiKey(provider, JSON.stringify(cleaned));
  if (provider !== 'gemini') {
    if (cleaned[0]) lsSet(browserKeyName(provider), cleaned[0]);
    else lsDel(browserKeyName(provider));
    return { encrypted: false };
  }
  lsSet(GEMINI_KEYS_NAME, JSON.stringify(cleaned));
  return { encrypted: false };
}

export async function clearPersistedKeys(provider: LlmProvider): Promise<void> {
  const b = bridge();
  if (b) {
    try {
      await b.clearApiKey(provider);
    } catch {
      // Best effort; UI drops its in-memory copy regardless.
    }
    return;
  }
  if (provider !== 'gemini') return clearPersistedKey(provider);
  lsDel(GEMINI_KEYS_NAME);
  lsDel(browserKeyName('gemini'));
}

export interface Prefs {
  provider: LlmProvider;
  model: string;
  linger: number;
}

export function loadPrefs(): Prefs {
  const providerValue = lsGet('converse:provider');
  const provider: LlmProvider = providerValue === 'gemini' ? 'gemini' : 'deepseek';
  const model = lsGet(LS_MODEL) ?? migrateLegacy(LS_MODEL) ?? '';
  const linger = Number(lsGet(LS_LINGER) ?? migrateLegacy(LS_LINGER) ?? '');
  return {
    provider,
    model: model || (provider === 'gemini' ? DEFAULT_GEMINI_MODEL : DEFAULT_MODEL),
    linger: linger >= 0.5 && linger <= 2 ? linger : DEFAULT_LINGER,
  };
}

export function savePrefs(provider: LlmProvider, model: string, linger: number): void {
  lsSet('converse:provider', provider);
  lsSet(LS_MODEL, model);
  lsSet(LS_LINGER, String(linger));
}
